/**
 * Abertura do painel de curadoria de IA a partir da linha do ativo.
 *
 * Espelha `crm/utils/clientRowActions.js`: o painel (`ia-suggestions-modal`) é domain-agnostic e
 * não conhece campo de nenhum domínio, então quem monta escopo e subtítulo no vocabulário do
 * consolidador é este helper.
 *
 * ★ O escopo é o CLIENTE + um FILTRO de domínio, não `id_entidade_alvo` do ativo. A fila "deste
 * ativo" inclui as sugestões das MOVIMENTAÇÕES dele, cuja PK é a da movimentação — filtrar no
 * backend por um id só deixava metade de fora, e o selo contava 2 enquanto o painel abria com 0.
 * O invariante continua o mesmo: o que o selo CONTA é exatamente o que o painel MOSTRA, e agora
 * as duas pontas usam o MESMO predicado.
 *
 * `dadosDoAceite` viaja junto porque o `Aprovar` do painel não passa por formulário: sem ele o
 * backend aplica só o `{classe}` da sugestão e a subclasse antiga fica órfã na linha. O painel usa
 * a função quando ela existe; sem ela (CRM), o aceite segue exatamente como hoje.
 *
 * O contexto leva a lista `ativos` mesmo assim: o `id_entidade_alvo` da sugestão continua sendo
 * quem decide qual ativo o formulário de edição abre, e não a linha de onde o painel saiu.
 */
import { MODAL } from '@constants/Text'
import { dadosDoAceitePorCategoria } from '@modules/consolidator/utils/dados-do-aceite/dados-do-aceite'
import {
  idDoAtivoDaSugestao,
  idsDosAtivos,
} from '@modules/consolidator/utils/suggestions-fila/suggestions-fila'
import {
  apresentacaoDaSugestao,
  textoDaSugestao,
} from '@modules/consolidator/utils/suggestion-apresentacao/suggestion-apresentacao'
import {
  ativoDaSugestao,
  contextoDaMovimentacao,
  movimentacaoComSugestao,
  movimentacaoDaSugestao,
} from '@modules/consolidator/utils/suggestion-to-movimentacao/suggestion-to-movimentacao'
import { CONTEXTO_DO_ATIVO } from '@modules/consolidator/constants/Text'
import { ehDeMovimentacao } from '@modules/consolidator/utils/suggestions-categoria/suggestions-categoria'
import { GlobalBus } from '@services/bus'

export const ESCOPO_CLIENTE = 'cliente'

// Degrada em VAZIO, nunca em "Ativo sem nome": o subtítulo some, em vez de a tela afirmar que o
// ativo não tem nome. Mesma regra da apresentação do card.
export const nomeDoAtivo = (asset) => {
  const ativo = asset || {}
  return ativo.nome_ativo || ativo.nome || ''
}

export const subtituloDoAtivo = (asset) => nomeDoAtivo(asset)

/**
 * O MESMO predicado que a contagem do selo usa: a sugestão é deste ativo se aponta o ativo, ou se
 * aponta uma movimentação dele. Exportado porque o selo e o painel PRECISAM compartilhá-lo — foi
 * tê-los separados que produziu "2 pendentes" abrindo um painel vazio.
 */
export const ehDesteAtivo = ({ asset, movimentacoes } = {}) => (suggestion) => {
  if (!asset || asset.id === null || asset.id === undefined) return false
  return idDoAtivoDaSugestao(suggestion, { movimentacoes }) === String(asset.id)
}

/**
 * A MESMA apresentacao da aba. Sem ela, o painel aberto pelo selo continuava titulando pelo patch
 * ("Valor unitario: 1473.08") e chamando de "Correcao de cadastro" uma correcao de PU — os dois
 * defeitos que a aba ja tinha corrigido. Superficie nova nao herda o acerto sozinha.
 */
export const apresentarNoPainel = ({ asset, movimentacoes }) => (suggestion) => {
  if (!ehDeMovimentacao(suggestion)) {
    return apresentacaoDaSugestao({ suggestion, ativo: asset, contexto: CONTEXTO_DO_ATIVO })
  }
  const mov = movimentacaoDaSugestao({ suggestion, indice: movimentacoes })
  return apresentacaoDaSugestao({
    suggestion,
    ativo: asset,
    contexto: contextoDaMovimentacao(mov),
  })
}

/**
 * O destino da edicao depende da CATEGORIA. Um `eventoEditar` unico mandava tudo para o formulario
 * do ativo — que nao acha o alvo de uma sugestao de movimentacao (a PK e de outra tabela) e fecha
 * em silencio. Aqui a sugestao de PU abre o modal de movimentacao, com a nota da IA junto.
 */
// Devolve SE abriu. O painel usa a resposta pra avisar na tela quando nao abriu: um `return` mudo
// aqui ja se disfarcou duas vezes de "o clique nao faz nada" — primeiro pela categoria errada, e
// depois porque a lista de ativos chegava vazia. Falha silenciosa nao vira bug reportavel.
export const editarDoPainel = ({ asset, ativosDoCliente, movimentacoes, cliente }) => (
  suggestion
) => {
  if (!ehDeMovimentacao(suggestion)) {
    GlobalBus.$emit(MODAL.CONSOLIDADOR_EDIT_ASSET_SUGGESTION, {
      suggestion,
      contexto: { asset, ativos: ativosDoCliente },
    })
    return true
  }
  const mov = movimentacaoDaSugestao({ suggestion, indice: movimentacoes })
  if (!mov) return false
  GlobalBus.$emit(
    MODAL.CADASTRAR_APORTE,
    { id: (cliente && cliente.id) || asset.id_cliente, name: cliente && cliente.nome_cliente },
    movimentacaoComSugestao({ mov, suggestion }),
    asset,
    true,
    textoDaSugestao(suggestion),
    // 6o argumento: a SUGESTAO em si. Sem ela o modal salva a movimentacao e a sugestao fica
    // Pendente para sempre — o caminho do cadastro ja aceitava, o de movimentacao nao.
    suggestion
  )
  return true
}

/**
 * Painel com as sugestoes do CONSOLIDADOR deste cliente — o destino do alerta da carteira, que fala
 * de N ativos. Redirecionar pra aba tirava o assessor da tela onde ele estava; aqui ele decide e
 * continua.
 *
 * ★ O filtro NAO e opcional. O painel busca pela API COMPARTILHADA (`apiSuggestions`), que devolve a
 * fila inteira do cliente — CRM, tarefas, transcricoes. Sem recorte, o alerta dizia "2 sugestoes" e
 * o painel abria com 73. Mesmo invariante do selo: o que a tela CONTA e o que o painel MOSTRA, e as
 * duas pontas saem do MESMO resolvedor.
 */
// O MESMO resolvedor da contagem: se ele acha o ativo, a sugestao e desta carteira.
export const ehDaCarteira = ({ movimentacoes, ativos } = {}) => (suggestion) =>
  idDoAtivoDaSugestao(suggestion, { movimentacoes, ativos }) !== null

export const openClientSuggestions = ({ cliente, ativos = [], movimentacoes = null } = {}) => {
  const idCliente = cliente && (cliente.id_cliente_app || cliente.id)
  if (idCliente === null || idCliente === undefined) return false
  GlobalBus.$emit(MODAL.IA_SUGGESTIONS, {
    scope: { tipo: ESCOPO_CLIENTE, id: idCliente },
    subtitulo: cliente.nome_cliente || cliente.nome || '',
    dadosDoAceite: dadosDoAceitePorCategoria,
    filtrar: ehDaCarteira({ movimentacoes, ativos: idsDosAtivos(ativos) }),
    apresentar: apresentarDaCarteira({ ativos, movimentacoes }),
    editarExterno: editarDaCarteira({ ativos, movimentacoes, cliente }),
  })
  return true
}

// O ativo de CADA sugestao sai do proprio de-para: no painel do cliente as sugestoes sao de varios
// ativos, entao a apresentacao nao pode fixar um so.
const ativoDaVez = ({ suggestion, ativos, movimentacoes }) => {
  if (ehDeMovimentacao(suggestion)) {
    return ativoDaSugestao({ suggestion, indice: movimentacoes })
  }
  const alvo = String(suggestion && suggestion.id_entidade_alvo)
  return (ativos || []).find((a) => a && String(a.id) === alvo) || null
}

export const apresentarDaCarteira = ({ ativos, movimentacoes }) => (suggestion) => {
  const ativo = ativoDaVez({ suggestion, ativos, movimentacoes })
  if (!ehDeMovimentacao(suggestion)) {
    return apresentacaoDaSugestao({ suggestion, ativo, contexto: CONTEXTO_DO_ATIVO })
  }
  const mov = movimentacaoDaSugestao({ suggestion, indice: movimentacoes })
  return apresentacaoDaSugestao({ suggestion, ativo, contexto: contextoDaMovimentacao(mov) })
}

export const editarDaCarteira = ({ ativos, movimentacoes, cliente }) => (suggestion) => {
  const ativo = ativoDaVez({ suggestion, ativos, movimentacoes })
  if (!ativo) return false
  return editarDoPainel({
    asset: ativo,
    ativosDoCliente: ativos,
    movimentacoes,
    cliente,
  })(suggestion)
}

export const openAssetSuggestions = (
  asset,
  ativosDoCliente = [],
  movimentacoes = null,
  cliente = null
) => {
  const idCliente = asset && asset.id_cliente
  if (idCliente === null || idCliente === undefined) return false
  GlobalBus.$emit(MODAL.IA_SUGGESTIONS, {
    scope: { tipo: ESCOPO_CLIENTE, id: idCliente },
    subtitulo: subtituloDoAtivo(asset),
    dadosDoAceite: dadosDoAceitePorCategoria,
    filtrar: ehDesteAtivo({ asset, movimentacoes }),
    apresentar: apresentarNoPainel({ asset, movimentacoes }),
    editarExterno: editarDoPainel({ asset, ativosDoCliente, movimentacoes, cliente }),
  })
  return true
}
